import { Box, Text, TextButton } from '@wix/design-system';
import { CycleUtils } from '@wix/package-picker-shared';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import { ConfirmSmall, GiftSmall } from '@wix/wix-ui-icons-common';
import React, { useEffect } from 'react';
import { extractNumericRate } from '../../../utils';
import { useCurrentModule } from '../../ModuleProvider';
import { CycleSelectorBadgeUi } from '../../CycleSelectorBadge/CycleSelectorBadgeUi';
const YEARLY_CYCLE_ID = `1-${PurchaseOffering.DurationUnit.YEAR}`;
const MONTHLY_CYCLE_ID = `1-${PurchaseOffering.DurationUnit.MONTH}`;
const CHECK_COLOR = '#44823A';
const BORDER_COLOR = '#CFD1DC';
const PRICE_BENEFIT_KEY = 'checkout.summary.yearly_upsell.benefit1';
const FREE_DOMAIN_BENEFIT_KEY = 'checkout.summary.yearly_upsell.benefit2';
const BENEFITS = [PRICE_BENEFIT_KEY, FREE_DOMAIN_BENEFIT_KEY];
const CheckIcon = () => /*#__PURE__*/React.createElement(ConfirmSmall, {
  color: CHECK_COLOR
});
export const ChangePlanCycle = _ref => {
  let {
    switchedViaBanner,
    onSwitchCycle
  } = _ref;
  const module = useCurrentModule();
  const selectedCycle = module.SDK.PackagePickerStore.useSelectedCycle();
  const isMonthly = selectedCycle ? CycleUtils.isMonthly(selectedCycle) : false;
  const showSwitchToYearly = isMonthly;
  const showSwitchToMonthly = !isMonthly && switchedViaBanner;
  if (showSwitchToYearly) {
    return /*#__PURE__*/React.createElement(SwitchToYearly, {
      onSwitchCycle: onSwitchCycle
    });
  }
  if (showSwitchToMonthly) {
    return /*#__PURE__*/React.createElement(SwitchToMonthly, {
      onSwitchCycle: onSwitchCycle
    });
  }
  return null;
};
function SwitchToYearly(_ref2) {
  var _yearlyCyclePricingDa, _yearlyCyclePricingDa2, _monthlyCyclePricingD;
  let {
    onSwitchCycle
  } = _ref2;
  const module = useCurrentModule();
  const selectedProductId = module.SDK.PackagePickerStore.useSelectedProductId();
  const {
    t
  } = module.internals.useTranslations();
  useEffect(() => {
    module.internals.bi.onSwitchCycleComponentLoad == null || module.internals.bi.onSwitchCycleComponentLoad({
      componentName: 'switch to yearly'
    });
  }, [module]);
  const {
    data: yearlyCyclePricingData
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchCyclePricing({
    productId: selectedProductId || '',
    cycleId: YEARLY_CYCLE_ID
  }, Boolean(selectedProductId));
  const {
    data: monthlyCyclePricingData
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchCyclePricing({
    productId: selectedProductId || '',
    cycleId: MONTHLY_CYCLE_ID
  }, Boolean(selectedProductId));
  const {
    data: product
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({
    productId: selectedProductId || ''
  }, Boolean(selectedProductId));
  const {
    data: discountData
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});
  const renderBadge = () => {
    var _savings$fixedAmount, _discountData$discoun;
    const {
      salePerCycle,
      hasSale
    } = product ?? {};
    const {
      savings,
      discountFixedAmount
    } = (yearlyCyclePricingData == null ? void 0 : yearlyCyclePricingData.cyclePricing) ?? {};
    const isSaleCycle = salePerCycle == null ? void 0 : salePerCycle[YEARLY_CYCLE_ID];
    const showSaleBadge = isSaleCycle && (discountFixedAmount == null ? void 0 : discountFixedAmount.formattedAmount);
    const showSavingsBadge = !showSaleBadge && (savings == null || (_savings$fixedAmount = savings.fixedAmount) == null ? void 0 : _savings$fixedAmount.amount) && !hasSale;
    if (!showSaleBadge && !showSavingsBadge) {
      return null;
    }
    const badgeText = showSaleBadge ? t('checkout.summary.yearlyUpsale.badge.discountLabel', {
      savingsAmount: discountFixedAmount.formattedAmount,
      rate: discountData != null && (_discountData$discoun = discountData.discount) != null && _discountData$discoun.rate ? extractNumericRate(discountData.discount.rate) : ''
    }) : t('checkout.summary.yearly_upsell.savings_badge.label', {
      savingsAmount: savings.fixedAmount.formattedAmount
    });
    return /*#__PURE__*/React.createElement(Box, {
      marginBottom: "10px"
    }, /*#__PURE__*/React.createElement(CycleSelectorBadgeUi, {
      text: badgeText,
      uppercase: false
    }));
  };
  const {
    hasSale
  } = product ?? {};
  const yearlyPrice = yearlyCyclePricingData == null || (_yearlyCyclePricingDa = yearlyCyclePricingData.cyclePricing) == null || (_yearlyCyclePricingDa = _yearlyCyclePricingDa.monthlyFinalPrice) == null ? void 0 : _yearlyCyclePricingDa.formattedAmount;
  const oldPrice = hasSale ? yearlyCyclePricingData == null || (_yearlyCyclePricingDa2 = yearlyCyclePricingData.cyclePricing) == null || (_yearlyCyclePricingDa2 = _yearlyCyclePricingDa2.monthlyFullPrice) == null ? void 0 : _yearlyCyclePricingDa2.formattedAmount : monthlyCyclePricingData == null || (_monthlyCyclePricingD = monthlyCyclePricingData.cyclePricing) == null || (_monthlyCyclePricingD = _monthlyCyclePricingD.monthlyFinalPrice) == null ? void 0 : _monthlyCyclePricingD.formattedAmount;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    padding: "18px",
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: "8px",
    marginBottom: "20px",
    width: "100%",
    boxSizing: "border-box",
    gap: "SP1"
  }, renderBadge(), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "bold"
  }, t('checkout.summary.yearly_upsell.benefits.title')), /*#__PURE__*/React.createElement(Box, {
    padding: "4px 0",
    direction: "vertical",
    gap: "SP1"
  }, BENEFITS.map(key => /*#__PURE__*/React.createElement(Box, {
    key: key,
    direction: "horizontal",
    verticalAlign: "top"
  }, /*#__PURE__*/React.createElement(CheckIcon, null), /*#__PURE__*/React.createElement(Text, {
    size: "small"
  }, t(key, {
    newPrice: yearlyPrice,
    oldPrice
  })), key === FREE_DOMAIN_BENEFIT_KEY && /*#__PURE__*/React.createElement(Box, {
    marginLeft: "2px"
  }, /*#__PURE__*/React.createElement(GiftSmall, null))))), /*#__PURE__*/React.createElement(TextButton, {
    size: "small",
    weight: "bold",
    onClick: () => {
      module.internals.bi.onSwitchCycleComponentClick == null || module.internals.bi.onSwitchCycleComponentClick({
        targetCycle: YEARLY_CYCLE_ID,
        componentName: 'switch to yearly'
      });
      onSwitchCycle(YEARLY_CYCLE_ID);
    }
  }, t('checkout.summary.yearly_upsell.switch_cycle.cta')));
}
function SwitchToMonthly(_ref3) {
  let {
    onSwitchCycle
  } = _ref3;
  const module = useCurrentModule();
  const {
    t
  } = module.internals.useTranslations();
  useEffect(() => {
    module.internals.bi.onSwitchCycleComponentLoad == null || module.internals.bi.onSwitchCycleComponentLoad({
      componentName: 'switch to monthly'
    });
  }, [module]);
  return /*#__PURE__*/React.createElement(Box, {
    paddingBottom: "SP3",
    marginTop: "-10px"
  }, /*#__PURE__*/React.createElement(TextButton, {
    size: "small",
    weight: "bold",
    onClick: () => {
      module.internals.bi.onSwitchCycleComponentClick == null || module.internals.bi.onSwitchCycleComponentClick({
        targetCycle: MONTHLY_CYCLE_ID,
        componentName: 'switch to monthly'
      });
      onSwitchCycle(MONTHLY_CYCLE_ID);
    }
  }, t('checkout.summary.yearly_upsell.switch_monthly_cycle.cta')));
}
//# sourceMappingURL=ChangePlanCycle.js.map